/**
 * System Prompt inspection sidebar for developer tooling.
 *
 * Shows the complete system prompt decomposed into labeled, collapsible
 * sections plus any per-turn injected messages (memory retrieval).
 * Refreshes every agent turn via SSE data-systemPrompt events.
 *
 * Follows ToolsPanel / DebugSidebar pattern.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, ScrollText, ChevronDown, ChevronRight, Copy, Check, Zap } from 'lucide-react';

export interface PromptSection {
  label: string;
  content: string;
}

export interface PromptInjection {
  source: string;
  role: string;
  content: string;
}

interface SystemPromptSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  sections: PromptSection[];
  hasCxsContext: boolean;
  serviceId: string | null;
  injections: PromptInjection[];
}

export function SystemPromptSidebar({
  isOpen,
  onClose,
  sections,
  hasCxsContext,
  serviceId,
  injections,
}: SystemPromptSidebarProps) {
  const [width, setWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['Base Prompt']),
  );
  const [copied, setCopied] = useState(false);

  const toggleSection = useCallback((label: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }, []);

  const handleCopyAll = useCallback(async () => {
    const fullPrompt = sections.map((s) => `## ${s.label}\n\n${s.content}`).join('\n\n');
    try {
      await navigator.clipboard.writeText(fullPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: noop
    }
  }, [sections]);

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      setWidth(Math.max(280, Math.min(700, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  if (!isOpen) return null;

  return (
    <div
      ref={sidebarRef}
      className="flex flex-col h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 relative"
      style={{ width: `${width}px` }}
      data-testid="system-prompt-sidebar"
    >
      {/* Resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-orange-400 transition-colors z-10"
        onMouseDown={handleMouseDown}
        style={{ backgroundColor: isResizing ? '#fb923c' : 'transparent' }}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <ScrollText size={18} className="text-orange-600" />
          <span className="font-medium text-gray-900 dark:text-white">System Prompt</span>
          {sections.length > 0 && (
            <span className="px-2 py-0.5 text-xs bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 rounded-full font-medium">
              {sections.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleCopyAll}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
            title="Copy full prompt"
            disabled={sections.length === 0}
          >
            {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
            title="Close sidebar"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* CXS Context badge */}
      {hasCxsContext && (
        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 bg-orange-50/50 dark:bg-orange-900/10">
          <span className="text-xs font-medium text-orange-700 dark:text-orange-400">
            Service: {serviceId || 'unknown'}
          </span>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 bg-gray-50/50 dark:bg-gray-800/20">
        {sections.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ScrollText className="w-10 h-10 text-gray-300 dark:text-gray-700 mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No prompt data yet</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Send a message to inspect.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sections.map((section) => {
              const isExpanded = expandedSections.has(section.label);
              return (
                <div
                  key={section.label}
                  className="rounded-lg bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 overflow-hidden"
                >
                  <button
                    onClick={() => toggleSection(section.label)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                      {section.label}
                    </span>
                    <span className="ml-auto text-xs text-gray-300 dark:text-gray-600">
                      {section.content.length}c
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-gray-50 dark:border-gray-700">
                      <pre className="font-mono text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words mt-2 max-h-80 overflow-y-auto">
                        {section.content.trim()}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Turn Injections */}
            {injections.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <Zap size={14} className="text-amber-500" />
                  <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    Turn Injections
                  </span>
                </div>
                <div className="space-y-1">
                  {injections.map((inj, idx) => (
                    <div
                      key={`${inj.source}-${idx}`}
                      className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800/50 border border-amber-100 dark:border-amber-800/50"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded">
                          {inj.source}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">{inj.role}</span>
                      </div>
                      <pre className="font-mono text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap break-words line-clamp-6">
                        {inj.content}
                      </pre>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
